import { navigateAction } from 'fluxible-router';
import { getSpotIdInjectorValue, setSpotIdInjectorValue } from '@spotim/fed-common';
import Signin from 'app/scripts/components/account/pages/login';
import ErrorNotifier from 'app/scripts/services/error-notifier';
import PageNotFound from 'app/scripts/components/common/page-not-found';
import { AnalyticsProvider } from 'app/scripts/services/analytics-provider';

import { LocalStorageItems } from '../../constants';
import SuperOwner from '../components/account/super-owner';
import ForgotPassword from '../components/account/pages/forgot-password';
import ResetPassword from '../components/account/pages/reset-password';
import { fetchSuperAdminData } from '../../settings/super-admin/superAdminActions';
import { fetchSsoData } from '../../settings/single-sign-on/ssoActions';

import { PageRoute } from 'app/scripts/configs/routeConstants';
import { dispatch, getState } from 'state/store';
import { fetchAuthorizedDomains } from '../../spots/spotsActions';

function updateSpotId(spotId, context) {
  if (getSpotIdInjectorValue() !== spotId) {
    let totango;
    setSpotIdInjectorValue(spotId);
    try {
      if (context && totango) {
        const spotName = getState().spots.map[spotId]?.name;

        window.totango_options = {
          service_id: 'SP-25247-01',
          account: {
            name: spotName,
            id: spotId
          }
        };
        totango.go();
        totango.track(`User Change spot to ${spotName}`);
      }
    } catch (e) {
      ErrorNotifier.notifyError(e);
    }
  }
}

async function updateSpot(context, payload, done) {
  const params = payload.params;
  const spotId = params.spot_id;
  updateSpotId(spotId);

  done();
}

function fetchPermissions(context, payload, done) {
  var params = payload.params;
  var spotId = params.spot_id;
  updateSpotId(spotId);

  dispatch(fetchAuthorizedDomains());
  done();
}

function navigateWithSpotId(context, payload, done) {
  var spotId = getSpotIdInjectorValue();
  var url = '';
  if (spotId) {
    url = payload.url.replace('spot/', `spot/${spotId}/`);
  } else {
    url = '/login';
  }
  context.executeAction(navigateAction, { url }, done);
}

export const analyticsEmbedPages = [
  PageRoute.AnalyticsEmbedEngagement,
  PageRoute.AnalyticsEmbedRevenue,
  PageRoute.AnalyticsEmbedModeration
];

async function fetchSuperAdminAction(context, payload, done) {
  const params = payload.params;
  const spotId = params.spot_id;

  dispatch(fetchSuperAdminData({ spotId, done }));
}

export default {
  home: {
    path: '/',
    method: 'get',
    page: 'home',
    title: 'OpenWeb',
    action: context => {
      context.executeAction(navigateAction, { url: '/login' });
    }
  },
  'super-owner': {
    path: '/internal/super-admin',
    method: 'get',
    page: 'super-owner',
    title: 'Set Spot',
    handler: SuperOwner
  },
  about: {
    path: '/',
    method: 'get',
    page: 'home',
    title: 'Home'
  },
  createAccount: {
    path: '/create-account',
    method: 'get',
    page: 'create-account',
    action: (context, payload, done) => {
      window.location = 'https://www.spot.im/request-an-invite/';
      done();
    }
  },
  verify: {
    path: '/login?spot_im_verify=:verifyType&spot_im_token_ticket=:ticket',
    method: 'get',
    page: 'verify',
    action: (context, payload, done) => {
      context.executeAction(navigateAction, { url: '/login' }, done);
    }
  },
  login: {
    path: '/login',
    method: 'get',
    page: 'login',
    title: 'Log In',
    handler: Signin
  },
  'network-login': {
    path: '/network/:networkName?',
    method: 'get',
    page: 'login',
    title: 'Log In',
    handler: Signin
  },
  forgotPassword: {
    path: '/forgot-password',
    method: 'get',
    page: 'forgot-password',
    title: 'Forgot Password',
    handler: ForgotPassword
  },
  resetPassword: {
    path: '/set-password',
    method: 'get',
    page: 'reset-password',
    title: 'Reset Password',
    handler: ResetPassword
  },
  twoFactorAuthentication: {
    page: 'two-factor-authentication',
    path: '/spot/:spot_id/two-factor-authentication',
    method: 'get',
    title: 'Verify Account'
  },
  'features-live-blog': {
    page: 'features-live-blog',
    path: '/spot/:spot_id?/features/live-blog',
    title: 'Live Blog',
    tabView: {
      menu_view: 'features-menu-viewed',
      first: 'features',
      secondary: 'live-blog'
    }
  },
  moderation: {
    path: '/spot/:spot_id?/moderation',
    method: 'get',
    page: 'moderation',
    title: 'Moderation',
    action: (context, payload, done) => {
      const { params } = payload;
      const { spot_id: spotId } = params;
      const moderationFilterCriteria = JSON.parse(
        localStorage.getItem(LocalStorageItems.ModerationFilterCriteria)
      );
      const queryParams = Object.entries(moderationFilterCriteria ?? {})
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      var url = `/spot/${spotId}/moderation/comments/folder/pending/tag/all?${queryParams}`;
      context.executeAction(navigateAction, { url }, done);
    }
  },
  'moderation-comments': {
    page: 'moderation-comments',
    method: 'get',
    path: '/spot/:spot_id?/moderation/comments',
    title: 'Comments Moderation',
    tabView: {
      menu_view: 'moderation-menu-viewed',
      first: 'moderation',
      secondary: 'comments'
    },
    action: async (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      updateSpotId(spotId, context);
      let url = `/spot/${spotId}/moderation/comments/folder/pending/tag/all`;
      context.executeAction(navigateAction, { url }, done);
    }
  },
  'moderation-articles': {
    page: 'moderation-articles',
    method: 'get',
    path: '/spot/:spot_id?/moderation/articles/:pageNum?',
    title: 'Moderation by Articles',
    tabView: {
      first: 'moderation',
      secondary: 'articles'
    }
  },
  'moderation-comments-folders': {
    page: 'moderation-comments-folders',
    method: 'get',
    path: '/spot/:spot_id?/moderation/comments/folder/:folder/tag/:tag/:pageNum?',
    title: 'Comments Moderation',
    tabView: {
      menu_view: 'moderation-menu-viewed',
      first: 'moderation',
      secondary: 'comments'
    }
  },
  'moderation-posts': {
    page: 'moderation-posts',
    method: 'get',
    path: '/spot/:spot_id?/moderation/posts',
    title: 'Posts Moderation',
    tabView: {
      menuView: 'moderation-menu-viewed',
      first: 'moderation',
      secondary: 'posts'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      updateSpotId(spotId, context);
      var url = `/spot/${spotId}/moderation/posts/folder/pending/tag/all`;
      context.executeAction(navigateAction, { url }, done);
    }
  },
  'moderation-posts-folders': {
    page: 'moderation-posts-folders',
    method: 'get',
    path: '/spot/:spot_id?/moderation/posts/folder/:folder/tag/:tag/:pageNum?',
    title: 'Posts Moderation',
    tabView: {
      menu_view: 'moderation-menu-viewed',
      first: 'moderation',
      secondary: 'posts'
    }
  },
  'preferences-policy': {
    page: 'preferences-policy',
    method: 'get',
    path: '/spot/:spot_id?/moderation/preferences',
    title: 'Moderation > Preferences',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'policy'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'moderation-users': {
    page: 'moderation-users',
    method: 'get',
    path: '/spot/:spot_id?/moderation/users',
    title: 'Users',
    action: (context, payload) => {
      var params = payload.params;
      var spotId = params.spot_id;
      updateSpotId(spotId);
      var url = `/spot/${spotId}/moderation/users/blacklisted`;
      context.executeAction(navigateAction, { url });
    }
  },
  'moderation-users-blacklisted': {
    page: 'moderation-users-blacklisted',
    method: 'get',
    path: '/spot/:spot_id?/moderation/users/blacklisted',
    title: 'Blocked Users',
    tabView: {
      menu_view: 'moderation-menu-viewed',
      first: 'moderation',
      secondary: 'users blacklist'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpotId(spotId);
        done();
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'moderation-users-whitelisted': {
    page: 'moderation-users-whitelisted',
    method: 'get',
    path: '/spot/:spot_id?/moderation/users/whitelisted',
    title: 'Allowed Users',
    tabView: {
      menu_view: 'moderation-menu-viewed',
      first: 'moderation',
      secondary: 'users whitelist'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpotId(spotId);
        done();
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'moderation-restricted-words': {
    page: 'moderation-restricted-words',
    method: 'get',
    path: '/spot/:spot_id?/moderation/restricted-words',
    title: 'Moderation > Restricted Words',
    tabView: {
      menu_view: 'moderation-menu-viewed',
      first: 'moderation',
      secondary: 'words'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpotId(spotId);
        done();
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'moderation-advanced': {
    page: 'moderation-advanced',
    method: 'get',
    path: '/spot/:spot_id?/moderation/advanced',
    title: 'Moderation > Advanced',
    tabView: {
      menu_view: 'moderation-menu-viewed',
      first: 'moderation',
      secondary: 'advanced'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpotId(spotId);
        done();
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  analytics: {
    page: 'analytics',
    method: 'get',
    path: '/spot/:spot_id?/analytics',
    title: 'Analytics',
    action: (context, payload) => {
      var params = payload.params;
      var spotId = params.spot_id;
      updateSpotId(spotId);
      var url = `/spot/${spotId}/analytics/overview`;
      context.executeAction(navigateAction, { url });
    }
  },
  'analytics-overview': {
    page: 'analytics-overview',
    method: 'get',
    path: '/spot/:spot_id?/analytics/overview',
    title: 'Analytics',
    tabView: {
      menu_view: 'analytics-menu-viewed',
      first: 'analytics',
      secondary: 'overview'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;

      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'analytics-monetization': {
    page: 'analytics-monetization',
    method: 'get',
    path: '/spot/:spot_id?/analytics/monetization',
    title: 'Analytics',
    tabView: {
      menu_view: 'analytics-menu-viewed',
      first: 'analytics',
      secondary: 'monetization'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      }
      done();
    }
  },
  [PageRoute.AnalyticsEmbedEngagement]: {
    page: PageRoute.AnalyticsEmbedEngagement,
    method: 'get',
    path: '/spot/:spot_id?/analytics/embed-engagement',
    title: 'Analytics',
    tabView: {
      first: 'analytics',
      secondary: 'engagement'
    }
  },
  [PageRoute.AnalyticsEmbedRevenue]: {
    page: PageRoute.AnalyticsEmbedRevenue,
    method: 'get',
    path: '/spot/:spot_id?/analytics/embed-revenue',
    title: 'Analytics',
    tabView: {
      first: 'analytics',
      secondary: 'revenue'
    }
  },
  [PageRoute.AnalyticsEmbedModeration]: {
    page: PageRoute.AnalyticsEmbedModeration,
    method: 'get',
    path: '/spot/:spot_id?/analytics/embed-moderation',
    title: 'Analytics',
    tabView: {
      first: 'analytics',
      secondary: 'moderation'
    }
  },
  'analytics-next': {
    page: 'analytics-next',
    method: 'get',
    path: '/spot/:spot_id?/analytics-next',
    title: 'Analytics',
    action: (_, payload) => {
      var params = payload.params;
      var spotId = params.spot_id;
      updateSpotId(spotId);
      AnalyticsProvider.track('analytics beta page clicked');
    }
  },
  'revenue-next': {
    page: 'revenue-next',
    method: 'get',
    path: '/spot/:spot_id?/revenue-next',
    title: 'Revenue',
    action: (_, payload) => {
      var params = payload.params;
      var spotId = params.spot_id;
      updateSpotId(spotId);
      AnalyticsProvider.track('revenue overview beta page clicked');
    }
  },
  'revenue-monetization': {
    page: 'revenue-monetization',
    method: 'get',
    path: '/spot/:spot_id?/revenue/monetization',
    title: 'Revenue',
    tabView: {
      menu_view: 'analytics-menu-viewed',
      first: 'revenue',
      secondary: 'monetization'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      }
      AnalyticsProvider.track('revenue table beta page clicked');

      done();
    }
  },
  preferences: {
    path: '/spot/:spot_id?/preferences',
    method: 'get',
    page: 'preferences',
    title: 'Settings',
    action: (context, payload) => {
      var params = payload.params;
      var spotId = params.spot_id;
      updateSpotId(spotId);
      var url = `/spot/${spotId}/preferences/general`;
      context.executeAction(navigateAction, { url });
    }
  },
  'preferences-general': {
    page: 'preferences-general',
    method: 'get',
    path: '/spot/:spot_id?/preferences/general',
    title: 'General Settings',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'general'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'preferences-design': {
    page: 'preferences-design',
    method: 'get',
    path: '/spot/:spot_id?/preferences/design',
    title: 'Design',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'design'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'preferences-premissions': {
    page: 'preferences-premissions',
    method: 'get',
    path: '/spot/:spot_id?/preferences/permissions',
    title: 'Permissions',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'team'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        done();
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'preferences-advanced': {
    page: 'preferences-advanced',
    method: 'get',
    path: '/spot/:spot_id?/preferences/advanced',
    title: 'Advanced',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'advanced'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        fetchPermissions(context, payload, done);
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'preferences-single-sign-on': {
    page: 'preferences-single-sign-on',
    method: 'get',
    path: '/spot/:spot_id?/preferences/single-sign-on',
    title: 'Single Sign On',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'single-sign-on'
    },
    action: async (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;

      dispatch(fetchSsoData());

      if (spotId) {
        fetchPermissions(context, payload, done);
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'preferences-super-admin-settings': {
    page: 'preferences-super-admin-settings',
    method: 'get',
    path: '/spot/:spot_id?/preferences/super-admin/settings',
    title: 'Super Admin Settings',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'system-admin'
    },
    action: fetchSuperAdminAction
  },
  'preferences-css-variables': {
    page: 'preferences-css-variables',
    method: 'get',
    path: '/spot/:spot_id?/preferences/css-variables',
    title: 'Css Variables',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'css-variables'
    },
    action: fetchSuperAdminAction
  },
  'preferences-conversation-feature-flags': {
    page: 'preferences-conversation-feature-flags',
    method: 'get',
    path: '/spot/:spot_id?/preferences/conversation-feature-flags',
    title: 'Css Variables',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'conversation-feature-flags'
    },
    action: fetchSuperAdminAction
  },
  'preferences-super-admin-products': {
    page: 'preferences-super-admin-products',
    method: 'get',
    path: '/spot/:spot_id?/preferences/super-admin/products',
    title: 'Super Admin Products',
    tabView: {
      menu_view: 'settings-menu-viewed',
      first: 'settings',
      secondary: 'system-admin'
    },
    action: fetchSuperAdminAction
  },
  features: {
    page: 'features',
    method: 'get',
    path: '/spot/:spot_id?/features',
    title: 'Social kit',
    action: (context, payload) => {
      var params = payload.params;
      var spotId = params.spot_id;
      updateSpotId(spotId);
      var url = `/spot/${spotId}/features/conversation`;
      context.executeAction(navigateAction, { url });
    }
  },
  'features-conversation': {
    page: 'features-conversation',
    method: 'get',
    path: '/spot/:spot_id?/features/conversation',
    title: 'Social kit',
    tabView: {
      menu_view: 'features-menu-viewed',
      first: 'features',
      secondary: 'social-kit'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'features-social': {
    page: 'features-social',
    method: 'get',
    path: '/spot/:spot_id?/features/social',
    title: 'Social reviews',
    tabView: {
      menu_view: 'features-menu-viewed',
      first: 'features',
      secondary: 'social-reviews'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'features-broadcast': {
    page: 'features-broadcast',
    method: 'get',
    path: '/spot/:spot_id?/features/broadcast',
    title: 'Broadcast',
    tabView: {
      menu_view: 'features-menu-viewed',
      first: 'features',
      secondary: 'broadcast'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpotId(spotId);

        done();
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'features-spotlight': {
    page: 'features-spotlight',
    method: 'get',
    path: '/spot/:spot_id?/features/spotlight',
    title: 'Spotlight',
    tabView: {
      menu_view: 'features-menu-viewed',
      first: 'features',
      secondary: 'spotlight'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'features-reactions': {
    page: 'features-reactions',
    method: 'get',
    path: '/spot/:spot_id?/features/reactions',
    title: 'Reactions',
    tabView: {
      menu_view: 'features-menu-viewed',
      first: 'features',
      secondary: 'reactions'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },

  'features-topic-tracker': {
    page: 'features-topic-tracker',
    method: 'get',
    path: '/spot/:spot_id?/features/topic-tracker',
    title: 'Topic Tracker',
    tabView: {
      menu_view: 'features-menu-viewed',
      first: 'features',
      secondary: 'Topic Tracker'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'features-floating-bell': {
    page: 'features-floating-bell',
    method: 'get',
    path: '/spot/:spot_id?/features/floating-bell',
    title: 'Floating Bell',
    tabView: {
      menu_view: 'features-menu-viewed',
      first: 'features',
      secondary: 'Floating Bell'
    },
    action: (context, payload, done) => {
      var params = payload.params;
      var spotId = params.spot_id;
      if (spotId) {
        updateSpot(context, payload, done);
      } else {
        navigateWithSpotId(context, payload, done);
      }
    }
  },
  'account-settings': {
    page: 'account-settings',
    method: 'get',
    path: '/settings/account',
    title: 'Account Settings'
  },
  'page-not-found': {
    page: 'page-not-found',
    method: 'get',
    path: '/page-not-found',
    title: 'Page Not Found',
    handler: PageNotFound
  }
};
